import { Currency } from 'types/Currencies';
import {
  getCurrencyCode,
  getPosCurrencyUnits,
  getSetting,
  getNumberFormat,
} from 'reducers/configs/settings';
import { roundCurrency } from 'utils';

import * as c from '../../constants/configs';
import { RootState } from '../index';

interface State {
  all: Currency[];
  default: Currency | null;
}

const currencyState: State = { all: [], default: null };

export const currency = (
  state = currencyState,
  { type, data }: { type: string; data: Currency[] },
): State => {
  switch (type) {
    case c.GET_CURRENCY_SUCCESS:
      return {
        ...state,
        all: data,
        default: data.find(c => Number(c.default)) ?? data[0],
      };
    default:
      return state;
  }
};

export default currency;

const cents = (...arr: number[]) => arr.map(c => c / 100);

export const getCurrencyDenominations = (code: string) => (
  state: RootState,
) => {
  const currencyCode = code ?? getCurrencyCode(state);
  const denominations = getPosCurrencyUnits(state);
  const {
    names = {},
    denominations: defaultDenoms,
    wholeFormat,
    centsFormat,
  } = getCurrency(currencyCode)(state);

  const res = denominations
    ? denominations.map(({ name, value }) => {
        /*
  Supported formats - 1) [500=200,100=100,10=10,0.50=0.5] (aka $display_value = $functional_value)
  so if you have denominations entered then pressing on 500 will actually add a payment of 200
  2) 100,50,25,10,5,1,0.1 - treats this as [100=100,50=50, etc]
*/
        return {
          value,
          mark:
            name ||
            names[value] ||
            (Math.abs(value) < 1
              ? centsFormat(value)
              : wholeFormat(value, { preserveInteger: true })),
        };
      })
    : defaultDenoms.map(d => ({
        value: d,
        mark:
          names[d] ||
          (Math.abs(d) < 1
            ? centsFormat(d)
            : wholeFormat(d, { preserveInteger: true })),
      }));
  return res;
};

export const getAllCurrencies = (state: RootState) =>
  state.configs.currency.all;

export const getCurrencyByCode = (code: string) => (state: RootState) =>
  getAllCurrencies(state).find(cur => cur.code === code);

export const getDefaultCurrencyObject = (state: RootState) =>
  state.configs.currency.default;

type FormatFn = (
  n: number | string,
  options?: {
    skipRounding?: boolean;
    roundDown?: boolean;
    preserveInteger?: boolean;
  },
) => string;
type UnformatFn = (str?: string) => number;

export interface PosCurrency {
  symbol: string;
  formatNoSymbol: FormatFn;
  unformatNoSymbol: UnformatFn;
  format: FormatFn;
  wholeFormat: FormatFn;
  centsFormat: FormatFn;
  names?: Record<number, string>;
  denominations: number[];
  isoNumCode: string;
}

export const getCurrency = (code: string) => (
  state: RootState,
): PosCurrency => {
  // numberFormat is a two character string
  const [decimalSep, thousandsSep] = getNumberFormat(state);
  const overwriteMoneyDecimals = getSetting('overwrite_money_decimals')(state);

  const format: FormatFn = (num, options) => {
    const n = typeof num === 'string' ? Number(num) : num;

    const fractionDigits = (function getFractionDigits() {
      // with preserveInteger, if the input is a whole value, we show no decimals
      if (options?.preserveInteger && Math.floor(n) === n) {
        return 0;
      }
      if (overwriteMoneyDecimals) {
        const overwrite = Number(overwriteMoneyDecimals);
        // Non integers are ignored - would use browser default of 3 decimal places
        if (Math.floor(overwrite) === overwrite) {
          // Out of range values are ignored - would throw errors
          if (0 <= overwrite && overwrite <= 100) {
            return overwrite;
          }
        }
      }
      // Default value (override missing or invalid)
      return 2;
    })();

    const roundedNum = options?.skipRounding
      ? num
      : roundCurrency(n, options?.roundDown);

    // prettier-ignore
    return roundedNum
      /* de-DE = 23.456.789,123456123 */
      .toLocaleString('de-DE', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      })
      .replace(/[,.]/g, (match) => ({
        '.': thousandsSep,
        ',': decimalSep,
      }[match]));
  };

  const unFormat: UnformatFn = str =>
    Number(
      (str ?? '')
        .toString()
        .split(thousandsSep)
        .map(tp => tp.split(decimalSep).join('.'))
        .join(''),
    );

  switch (code) {
    case 'USD':
      return {
        symbol: '$',
        formatNoSymbol: format,
        unformatNoSymbol: unFormat,
        format: (n, options) => `$${format(n, options)}`,
        wholeFormat: (n, options) => `$${format(n, options)}`,
        centsFormat: (n, options) => `$${format(n, options)}`,
        names: {
          0.25: 'quarter',
          0.1: 'dime',
          0.05: 'nickel',
          0.01: 'penny',
        },
        denominations: [100, 50, 20, 10, 5, 1, ...cents(50, 25, 10, 5, 1)],
        isoNumCode: '840',
      };
    case 'CAD':
      return {
        symbol: '$',
        format: (n, options) => `$${format(n, options)}`,
        formatNoSymbol: format,
        unformatNoSymbol: unFormat,
        wholeFormat: (n, options) => `$${format(n, options)}`,
        centsFormat: n => `${Number(n) * 100}¢`,
        denominations: [100, 50, 20, 10, 5, 2, 1, ...cents(50, 25, 10, 5)],
        isoNumCode: '124',
      };
    case 'AUD':
      return {
        symbol: '$',
        format: (n, options) => `$${format(n, options)}`,
        formatNoSymbol: format,
        unformatNoSymbol: unFormat,
        wholeFormat: (n, options) => `$${format(n, options)}`,
        centsFormat: n => `${Number(n) * 100}c`,
        denominations: [100, 50, 20, 10, 5, 2, 1, ...cents(50, 20, 10, 5)],
        isoNumCode: '036',
      };
    case 'GBP':
      return {
        symbol: '£',
        format: (n, options) => `£${format(n, options)}`,
        formatNoSymbol: format,
        unformatNoSymbol: unFormat,
        wholeFormat: (n, options) => `£${format(n, options)}`,
        centsFormat: n => `${Number(n) * 100}p`,
        // prettier-ignore
        denominations: [100, 50, 20, 10, 5, 2, 1, ...cents(50, 20, 10, 5, 2, 1)],
        isoNumCode: '826',
      };
    case 'NZD':
      return {
        symbol: '$',
        format: (n, options) => `$${format(n, options)}`,
        formatNoSymbol: format,
        unformatNoSymbol: unFormat,
        wholeFormat: (n, options) => `$${format(n, options)}`,
        centsFormat: n => `${Number(n) * 100}c`,
        denominations: [100, 50, 20, 10, 5, 2, 1, ...cents(50, 20, 10)],
        isoNumCode: '554',
      };
    case 'DKK':
      return {
        symbol: 'kr.',
        format: (n, options) => `${format(n, options)}kr.`,
        formatNoSymbol: format,
        unformatNoSymbol: unFormat,
        wholeFormat: (n, options) => `${format(n, options)}kr.`,
        centsFormat: n => `${Number(n) * 100}-øre`,
        denominations: [1000, 500, 200, 100, 50, 20, 10, 5, 2, 1, ...cents(50)],
        isoNumCode: '208',
      };
    case 'SGD':
      return {
        symbol: '$',
        format: (n, options) => `$${format(n, options)}`,
        formatNoSymbol: format,
        unformatNoSymbol: unFormat,
        wholeFormat: (n, options) => `$${format(n, options)}`,
        centsFormat: n => `${Number(n) * 100}c`,
        // prettier-ignore
        denominations: [1000, 500, 100, 50, 20, 10, 5, 2, 1, ...cents(50, 20, 10, 5)],
        isoNumCode: '702',
      };
    case 'EUR':
      return {
        symbol: '€',
        format: (n, options) => `${format(n, options)}€`,
        formatNoSymbol: format,
        unformatNoSymbol: unFormat,
        wholeFormat: (n, options) => `${format(n, options)}€`,
        centsFormat: (n, options) => `${format(n, options)}€`,
        // prettier-ignore
        denominations: [100, 50, 20, 10, 5, 2, 1, ...cents(50, 20, 10, 5, 2, 1)],
        isoNumCode: '978',
      };
    case 'CZK':
      return {
        symbol: 'Kč',
        format: (n, options) => `${format(n, options)}Kč`,
        formatNoSymbol: format,
        unformatNoSymbol: unFormat,
        wholeFormat: (n, options) => `${format(n, options)}Kč`,
        centsFormat: (n, options) => `${format(n, options)}Kč`,
        // prettier-ignore
        denominations: [5000,2000,1000,500,200,100, 50, 20, 10, 5, 2, 1],
        isoNumCode: '203',
      };
    case 'CLP':
      return {
        symbol: '$',
        format: (n, options) => `$${format(n, options)}`,
        formatNoSymbol: format,
        unformatNoSymbol: unFormat,
        wholeFormat: (n, options) => `$${format(n, options)}`,
        centsFormat: (n, options) => `$${format(n, options)}`,
        // prettier-ignore
        denominations: [20000, 10000, 5000, 2000, 1000, 500, 100, 50, 10, 5, 1],
        isoNumCode: '152',
      };
    default:
      return {
        symbol: code,
        format: (n, options) => `${format(n, options)}${code}`,
        formatNoSymbol: format,
        unformatNoSymbol: unFormat,
        wholeFormat: (n, options) => format(n, options),
        centsFormat: (n, options) => format(n, options),
        denominations: [
          500,
          200,
          100,
          50,
          20,
          10,
          5,
          2,
          1,
          ...cents(50, 20, 10, 5, 2),
        ],
        isoNumCode: '',
      };
  }
};
